/**
 * O ESTADO DE PUBLICAÇÃO DE CADA ITEM DO ACERVO.
 *
 * `T2.25` é o ponto mais detalhado do assunto acervo, e a parte que costuma passar batido é o
 * meio da frase:
 *
 *   *"eu acho que tem que ter uma parte de acervo pessoal, até porque **você até definiu QUAL
 *   DESSE SEU ACERVO É PÚBLICO**, que cai nessa biblioteca, ou que não seja público, seja visível
 *   para plataforma. A pessoa talvez precise de um acesso específico, mas ela consegue ver que
 *   aquele material existe de alguma forma ali. **E QUAL DESSE MATERIAL ELE É SEU ACERVO
 *   PESSOAL.** Então biblioteca não supre essa necessidade, precisa ter outras coisas."*
 *
 * Ou seja: o acervo não é só a lista do que você fez. É a lista **com o estado de cada um** — o
 * que está público, o que não está. Uma lista sem isso responde "o que eu criei" e não responde
 * "o que dele as outras pessoas veem", que é a metade que ele cobra.
 *
 * ── OS TRÊS ESTADOS, E O TERCEIRO SÓ EXISTE DESDE HOJE ─────────────────────────────────────
 *
 * Aos 03:35 ele descreve TRÊS visibilidades, e não duas: público · não-público-mas-visível-que-
 * existe-com-acesso-específico · privado. O terceiro reapareceu onze minutos depois, aos 15:09,
 * amarrado a instituição (`T2.63`: *"podem até aparecer um preview deles, mas só vão ser visíveis
 * para um educador ou aluno daquela instituição"*).
 *
 * O `CONTEXTO-PL-T2.8.md` já tinha registrado que os dois eram a mesma ideia. Então os três
 * estados aqui são:
 *
 *   guardado    só você. Nunca foi publicado, ou foi despublicado.
 *   publicado   está na biblioteca aberta, qualquer pessoa com conta lê.
 *   da-escola   está na biblioteca com escopo de instituição: a ficha é pública (o preview) e as
 *               páginas exigem vínculo ativo.
 *
 * ── POR QUE ISTO É UMA LEITURA A MAIS, E NÃO UM CAMPO NO QUADRINHO ─────────────────────────
 *
 * O estado de publicação mora em `biblioteca/{id}`, e não em `users/{uid}/comics/{id}`. Duplicá-lo
 * nos dois seria exatamente o que as regras da biblioteca já recusaram uma vez, e pela mesma
 * razão: dois lugares com a mesma verdade saem de sincronia no primeiro despublicar.
 *
 * O custo é uma leitura por quadrinho ao abrir o acervo. Aceitável, e a alternativa — uma
 * listagem filtrada por autor — exigiria uma consulta que as regras da biblioteca negam para quem
 * não é educador da instituição, e falharia inteira por causa de um item.
 */
import Firebase from "../../api/firebase.class";

export type EstadoDePublicacao = "guardado" | "publicado" | "da-escola";

export interface SituacaoDoItem {
  estado: EstadoDePublicacao;
  finalizado: boolean;
}

export const ROTULO_ESTADO: Record<EstadoDePublicacao, string> = {
  guardado: "só você vê",
  publicado: "na biblioteca",
  "da-escola": "só a sua escola vê",
};

export const AJUDA_ESTADO: Record<EstadoDePublicacao, string> = {
  guardado: "Este quadrinho não está publicado. Ninguém além de você o vê.",
  publicado: "Está na biblioteca aberta: qualquer pessoa com conta pode ler.",
  "da-escola":
    "Está na biblioteca da sua instituição. Outras pessoas veem que ele existe, e só quem é da escola pode ler.",
};

/**
 * Lê a situação de um quadrinho na biblioteca.
 *
 * Devolve `guardado` quando não há ficha — que é o caso comum, e não é erro: a maioria dos
 * quadrinhos nunca foi publicada. Por isso a falha de leitura também cai em `guardado` em vez de
 * quebrar a lista: um acervo que não abre porque uma consulta falhou é pior do que um acervo que
 * mostra um selo otimista a menos.
 */
export async function situacaoDoItem(comicId: string | undefined): Promise<SituacaoDoItem> {
  const guardado: SituacaoDoItem = { estado: "guardado", finalizado: false };
  if (!comicId) return guardado;

  try {
    const ficha = (await Firebase.getDocument("biblioteca", comicId)) as
      | { publicacao?: { estado?: string; finalizado?: boolean; instituicaoId?: string } }
      | null;
    if (!ficha?.publicacao) return guardado;

    const finalizado = !!ficha.publicacao.finalizado;
    if (ficha.publicacao.estado !== "publicado") return { estado: "guardado", finalizado };
    if (ficha.publicacao.instituicaoId) return { estado: "da-escola", finalizado };
    return { estado: "publicado", finalizado };
  } catch {
    return guardado;
  }
}

/** A situação de vários, em paralelo. O acervo tem poucos itens; serializar aqui seria gratuito. */
export async function situacoesDosItens(
  ids: (string | undefined)[]
): Promise<Record<string, SituacaoDoItem>> {
  const pares = await Promise.all(
    ids.filter(Boolean).map(async (id) => [id as string, await situacaoDoItem(id)] as const)
  );
  return Object.fromEntries(pares);
}
